import { BaseDataService } from './BaseDataService';
import { Team, Organization, PagedData } from '../../models';
import { NotificationService } from '../../services';
import { OpenBlobFile } from '../../utilities';

export class TeamDataService extends BaseDataService<Team> {
    constructor(notificationService: NotificationService) {
        super('Team', 'Teams', notificationService);
    }

    public getByOrganization(organization: Organization): Promise<Team[]> {
        const endpoint = `${this.endpointRoot}/byOrganization/${organization.id}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error('Network response was not ok.');
        });
    }

    public getByOrganizationPaged(organization: Organization, page: number, pageSize: number, filter?: string, sortProperty?: string, sortDirection?: string): Promise<PagedData<Team>> {
        let endpoint = `${this.endpointRoot}/byOrganizationPaged/${organization.id}?page=${page}&pageSize=${pageSize}`;
        const init = this.buildRequestInit('GET');

        if (filter !== undefined) {
            endpoint = `${endpoint}&filter=${filter}`;
        }

        if (sortProperty !== undefined) {
            endpoint = `${endpoint}&sortProperty=${sortProperty}`;

            if (sortDirection !== undefined) {
                endpoint = `${endpoint}&sortDirection=${sortDirection}`;
            } else {
                endpoint = `${endpoint}&sortDirection=asc`;
            }
        }

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error('Network response was not ok.');
        });
    }

    calculateMidRanges(teamId: number) {
        const endpoint = `${this.endpointRoot}/midranges/${teamId}`;
        const init = this.buildRequestInit('POST');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving teams.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    calculateTeamReport(teamId: number) {
        const endpoint = `${this.endpointRoot}/calculateTeamReport/${teamId}`;
        const init = this.buildRequestInit('POST');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving teams.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    getTeamScoresReport(teamId: number) {
        const endpoint = `${this.endpointRoot}/TeamScoresReport/${teamId}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving team scores.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            return OpenBlobFile(blobData, 'teamScoresReport.xlsx');
        });
    }

    queueTeamScoresReport(teamId: number) {
        const endpoint = `${this.endpointRoot}/TeamScoresReportRequests/${teamId}`;
        const init = this.buildRequestInit('POST');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                this.notificationService.addInfo("A team scores report was added to the queue.");
            } else {
                const message = "A team scores report could not be added to the queue.";
                this.notificationService.addError(message);
            }
        });
    }

    createUserBatch = async (teamId: number, csvFile: string) => {
        const endpoint = `${this.apiRoot}/UserBatch`;
        const init = this.buildRequestInit('POST');

        const data = new FormData();
        data.append('teamId', teamId.toString());
        data.append('file', csvFile)
        init.body = data;

        let response = await fetch(endpoint, init);
        if (response.status === 200) {
            return await response.json();
        }
        else {
            return {
                fileError: await response.text()
            };
        }
    }
}


// WEBPACK FOOTER //
// ./src/services/api/TeamData.tsx